import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronRight, Download, Folder as FolderIcon, FileText } from "lucide-react";
import { formatSize } from "../features/files/tree";

/**
 * Ein Ordner samt Inhalt hinter einem Link — ohne Anmeldung.
 *
 * Der Fall, den man meint, wenn man jemandem etwas zeigen will: „schau dir
 * das an" betrifft selten genau eine Datei. Der Link gibt den Ordner und
 * alles darunter heraus, ohne dass etwas davon öffentlich gelistet würde —
 * und er gilt nie nach oben: was daneben oder darüber liegt, bleibt
 * unsichtbar.
 */

type Ordner = { id: string; parent_id: string; name: string };
type Datei = {
  id: string; folder_id: string; name: string; size: number;
  content_type: string; updated_at: string;
};
type Antwort = { name: string; root: string; folders: Ordner[]; files: Datei[] };

export function SharedFolder() {
  const { token = "" } = useParams();
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);

  useEffect(() => {
    let leben = true;
    void (async () => {
      try {
        const r = await fetch(`/api/public/d/${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error(r.status === 404 ? "This link does not exist." : r.statusText);
        const a = (await r.json()) as Antwort;
        if (!leben) return;
        setDaten(a);
        setOffen(a.root);
      } catch (e) {
        if (leben) setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { leben = false; };
  }, [token]);

  /** Der Weg von der Wurzel bis zum offenen Ordner — mehr Navigation braucht
   *  es nicht, es ist ein geteilter Ausschnitt und kein Dateimanager. */
  const pfad = useMemo(() => {
    if (!daten || !offen) return [] as Ordner[];
    const byId = new Map(daten.folders.map((o) => [o.id, o]));
    const raus: Ordner[] = [];
    let jetzt = byId.get(offen);
    while (jetzt) {
      raus.unshift(jetzt);
      if (jetzt.id === daten.root) break;
      jetzt = byId.get(jetzt.parent_id);
    }
    return raus;
  }, [daten, offen]);

  if (fehler) return <main className="shared-file"><p className="error">{fehler}</p></main>;
  if (!daten) return <main className="shared-file"><p className="muted">Loading…</p></main>;

  const unter = daten.folders.filter((o) => o.parent_id === offen);
  const drin = daten.files.filter((d) => d.folder_id === offen);

  return (
    <main className="shared-file">
      <header className="shared-kopf">
        <FolderIcon className="icon-svg" />
        <h1>{daten.name}</h1>
      </header>

      <nav className="shared-pfad">
        {pfad.map((o, i) => (
          <span key={o.id}>
            {i > 0 && <ChevronRight className="icon-svg" />}
            <button type="button" onClick={() => setOffen(o.id)}>{o.name}</button>
          </span>
        ))}
      </nav>

      <ul className="shared-liste">
        {unter.map((o) => (
          <li key={o.id}>
            <button type="button" onClick={() => setOffen(o.id)}>
              <FolderIcon className="icon-svg" />
              <span>{o.name}</span>
            </button>
          </li>
        ))}
        {drin.map((d) => (
          <li key={d.id}>
            <a href={`/api/public/d/${encodeURIComponent(token)}/raw/${d.id}`}
               target="_blank" rel="noreferrer">
              <FileText className="icon-svg" />
              <span>{d.name}</span>
              <span className="muted">{formatSize(d.size)}</span>
              <Download className="icon-svg" />
            </a>
          </li>
        ))}
        {unter.length === 0 && drin.length === 0 && (
          <li className="muted">This folder is empty.</li>
        )}
      </ul>
    </main>
  );
}
